import { Editor } from '@tiptap/core';
import { RootState, store } from 'modules/Editor/store';
import { selectWidget } from 'modules/Editor/store/infographSelector';
import { WidgetId } from 'types/idTypes';
import { NewWidget } from 'widgets/Widget.types';

import {
  computeTextBoundingBoxHeight,
  findFirstLineHeight,
  generateDefaultData,
  getFirstCharacterTextMarks,
  getFontStyleFromTextMarks,
} from '../../TextWidget.helpers';
import { TextWidgetData } from '../../TextWidget.types';
import { NEW_TEXT_PADDING } from './ImproveText.config';

/**
 * Create new widget data with the generated text. Copies style from `sourceWidgetId`
 * The new widget will appear below the current widget
 *
 * @param editor
 * @param generatedText text content of new widget
 * @param sourceWidgetId source widget id to copy styles from
 * @returns
 */
export const generateImprovedTextWidgetData = (
  editor: Editor | null,
  generatedText: string,
  sourceWidgetId: WidgetId,
) => {
  if (!editor) return;
  const widgetHeight = editor.view.dom.clientHeight ?? 0;

  const storeState = store.getState() as RootState;
  const widgetData = selectWidget(sourceWidgetId)(storeState);
  if (!widgetData) return;

  const { widthPx, topPx, leftPx, textTag, proseMirrorData, letterSpacing, effect } = widgetData as TextWidgetData;
  const firstCharacterMarks = getFirstCharacterTextMarks(proseMirrorData);
  const fontStyle = getFontStyleFromTextMarks(firstCharacterMarks);
  const { fontColor, fontSize, fontFamily, isBold, isItalic, isUnderline } = fontStyle;
  const lineHeight = findFirstLineHeight(editor);

  return generateDefaultData(
    'paragraph',
    generatedText,
    false,
    {
      widthPx,
      heightPx: computeTextBoundingBoxHeight(widthPx, generatedText, fontStyle, lineHeight, letterSpacing),
      leftPx,
      // new widget should appear below the current widget
      topPx: widgetHeight + topPx + NEW_TEXT_PADDING,
      textTag,
      letterSpacing,
      effect,
    },
    {
      fontFamily,
      isBold,
      isItalic,
      isUnderline,
      lineHeight: findFirstLineHeight(editor),
      fontSize: typeof fontSize === 'number' ? fontSize : parseInt(fontSize),
      color: fontColor,
    },
  ) as NewWidget;
};
